<script setup lang="ts">
import { computed, ref, watch, type Component } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { ArrowLeft, ArrowRight, Sparkles, Rocket } from 'lucide-vue-next';

import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useUiStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/auth';

import WelcomeArtIntro from './art/WelcomeArtIntro.vue';
import WelcomeArtProjects from './art/WelcomeArtProjects.vue';
import WelcomeArtGoals from './art/WelcomeArtGoals.vue';
import WelcomeArtCalls from './art/WelcomeArtCalls.vue';
import WelcomeArtCreate from './art/WelcomeArtCreate.vue';

const ui = useUiStore();
const auth = useAuthStore();
const router = useRouter();
const { t } = useI18n();

const STEPS = ['intro', 'projects', 'goals', 'calls', 'create'] as const;
type StepId = (typeof STEPS)[number];

const ART: Record<StepId, Component> = {
  intro: WelcomeArtIntro,
  projects: WelcomeArtProjects,
  goals: WelcomeArtGoals,
  calls: WelcomeArtCalls,
  create: WelcomeArtCreate,
};

const stepIndex = ref(0);

const open = computed({
  get: () => ui.welcomeGuideOpen,
  set: (v: boolean) => {
    if (v) ui.openWelcomeGuide();
    else closeAndMarkSeen();
  },
});

const currentStep = computed<StepId>(() => STEPS[stepIndex.value] ?? 'intro');
const isFirst = computed(() => stepIndex.value === 0);
const isLast = computed(() => stepIndex.value === STEPS.length - 1);

watch(
  () => ui.welcomeGuideOpen,
  (isOpen) => {
    if (isOpen) stepIndex.value = 0;
  },
);

function userKey(): string | undefined {
  return auth.user?.id ?? auth.devShim?.userId;
}

function closeAndMarkSeen(): void {
  ui.markWelcomeGuideSeen(userKey());
  ui.closeWelcomeGuide();
}

function next(): void {
  if (stepIndex.value < STEPS.length - 1) stepIndex.value += 1;
}

function prev(): void {
  if (stepIndex.value > 0) stepIndex.value -= 1;
}

function gotoStep(i: number): void {
  if (i >= 0 && i < STEPS.length) stepIndex.value = i;
}

function finish(): void {
  closeAndMarkSeen();
  void router.push({ name: 'onboarding-entry' });
}
</script>

<template>
  <Dialog
    :open="open"
    @update:open="open = $event"
    class="max-w-2xl overflow-hidden p-0"
    hide-close
  >
    <div class="relative">
      <!-- decorative top gradient bar -->
      <div
        class="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-accent via-fuchsia-500 to-amber-400 opacity-80"
        aria-hidden="true"
      />

      <!-- illustration stage -->
      <div
        class="relative h-56 w-full overflow-hidden bg-gradient-to-br from-accent/15 via-fuchsia-500/10 to-amber-400/10"
      >
        <!-- soft blurred halos -->
        <div
          class="pointer-events-none absolute -top-16 -left-10 h-48 w-48 rounded-full bg-accent/30 blur-3xl"
          aria-hidden="true"
        />
        <div
          class="pointer-events-none absolute -bottom-20 -right-12 h-56 w-56 rounded-full bg-fuchsia-500/25 blur-3xl"
          aria-hidden="true"
        />
        <div
          class="pointer-events-none absolute top-10 right-10 h-32 w-32 rounded-full bg-amber-300/20 blur-2xl"
          aria-hidden="true"
        />

        <!-- per-step art -->
        <transition
          enter-active-class="transition duration-300 ease-apple"
          enter-from-class="opacity-0 translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-200 ease-apple"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
          mode="out-in"
        >
          <component :is="ART[currentStep]" :key="currentStep" />
        </transition>
      </div>

      <!-- body -->
      <div class="px-8 pt-6 pb-2">
        <transition
          enter-active-class="transition duration-300 ease-apple"
          enter-from-class="opacity-0 translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-apple"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
          mode="out-in"
        >
          <div :key="currentStep">
            <div class="flex items-center gap-2 mb-2">
              <span
                class="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-medium text-accent"
              >
                <Sparkles class="h-3 w-3" />
                {{ t(`welcomeGuide.steps.${currentStep}.eyebrow`) }}
              </span>
              <span class="text-micro text-text-muted">
                {{ stepIndex + 1 }} / {{ STEPS.length }}
              </span>
            </div>
            <h2 class="text-display-sm text-text mb-2">
              {{ t(`welcomeGuide.steps.${currentStep}.title`) }}
            </h2>
            <p class="text-body text-text-muted">
              {{ t(`welcomeGuide.steps.${currentStep}.body`) }}
            </p>
          </div>
        </transition>
      </div>

      <!-- footer / nav -->
      <div class="flex items-center justify-between gap-4 px-8 py-5">
        <!-- step dots -->
        <div class="flex items-center gap-1.5">
          <button
            v-for="(s, i) in STEPS"
            :key="s"
            type="button"
            class="h-1.5 rounded-full transition-all duration-200 ease-apple"
            :class="
              i === stepIndex
                ? 'w-6 bg-accent'
                : i < stepIndex
                  ? 'w-1.5 bg-accent/60'
                  : 'w-1.5 bg-border-strong'
            "
            :aria-label="t('welcomeGuide.gotoStep', { n: i + 1 })"
            @click="gotoStep(i)"
          />
        </div>

        <div class="flex items-center gap-2">
          <Button
            v-if="!isFirst"
            variant="ghost"
            size="sm"
            @click="prev"
          >
            <ArrowLeft class="h-4 w-4" />
            {{ t('welcomeGuide.back') }}
          </Button>
          <Button
            v-if="!isLast"
            variant="subtle"
            size="sm"
            @click="closeAndMarkSeen"
          >
            {{ t('welcomeGuide.skip') }}
          </Button>
          <Button
            v-if="!isLast"
            variant="primary"
            size="sm"
            @click="next"
          >
            {{ t('welcomeGuide.next') }}
            <ArrowRight class="h-4 w-4" />
          </Button>
          <Button
            v-else
            variant="primary"
            size="md"
            @click="finish"
          >
            <Rocket class="h-4 w-4" />
            {{ t('welcomeGuide.createAgent') }}
          </Button>
        </div>
      </div>
    </div>
  </Dialog>
</template>
